import React, { useMemo } from "react";

import * as THREE from "three";

import vertexShader from "../shaders/common.vert";

import fragmentShader from "../shaders/toon.frag";

export default function ToonMaterial({
  surface,

  lightDirection = [0.5, 0.8, 1.0],

  lightColor = "#ffffff",
  lightIntensity = 1.0,
  ambientIntensity = 0.15,

  bands = 4,
}) {
  const uniforms = useMemo(
    () => ({
      /*
       * Lighting.
       */
      uLightDirection: {
        value: new THREE.Vector3(),
      },

      uLightColor: {
        value: new THREE.Color(),
      },

      uLightIntensity: {
        value: 1.0,
      },

      uAmbientIntensity: {
        value: 0.15,
      },

      /*
       * Base surface.
       */
      uBaseColor: {
        value: new THREE.Color(),
      },

      uBaseTexture: {
        value: null,
      },

      uHasBaseTexture: {
        value: false,
      },

      /*
       * Normal map.
       */
      uNormalMap: {
        value: null,
      },

      uHasNormalMap: {
        value: false,
      },

      uNormalStrength: {
        value: 1.0,
      },

      /*
       * Height map.
       */
      uHeightMap: {
        value: null,
      },

      uHasHeightMap: {
        value: false,
      },

      uHeightStrength: {
        value: 0.05,
      },

      /*
       * Ambient occlusion.
       */
      uAOMap: {
        value: null,
      },

      uHasAOMap: {
        value: false,
      },

      /*
       * Emission.
       */
      uEmissiveColor: {
        value: new THREE.Color(),
      },

      uEmissiveMap: {
        value: null,
      },

      uHasEmissiveMap: {
        value: false,
      },

      /*
       * Shared UV transform.
       */
      uTextureScale: {
        value: new THREE.Vector2(1, 1),
      },

      uTextureRotation: {
        value: 0.0,
      },

      /*
       * Toon.
       */
      uBands: {
        value: 4.0,
      },
    }),
    [],
  );

  /*
   * Lighting.
   */
  uniforms.uLightDirection.value.set(...lightDirection).normalize();

  uniforms.uLightColor.value.set(lightColor);

  uniforms.uLightIntensity.value = lightIntensity;

  uniforms.uAmbientIntensity.value = ambientIntensity;

  /*
   * Surface.
   */
  uniforms.uBaseColor.value.set(surface.baseColor);

  uniforms.uBaseTexture.value = surface.baseTexture;

  uniforms.uHasBaseTexture.value = Boolean(surface.baseTexture);

  /*
   * Normal.
   */
  uniforms.uNormalMap.value = surface.normalMap;

  uniforms.uHasNormalMap.value = Boolean(surface.normalMap);

  uniforms.uNormalStrength.value = surface.normalStrength;

  /*
   * Height.
   */
  uniforms.uHeightMap.value = surface.heightMap;

  uniforms.uHasHeightMap.value = Boolean(surface.heightMap);

  uniforms.uHeightStrength.value = surface.heightStrength;

  /*
   * AO.
   */
  uniforms.uAOMap.value = surface.aoMap;

  uniforms.uHasAOMap.value = Boolean(surface.aoMap);

  /*
   * Emission.
   */
  uniforms.uEmissiveColor.value.set(surface.emissiveColor);

  uniforms.uEmissiveMap.value = surface.emissiveMap;

  uniforms.uHasEmissiveMap.value = Boolean(surface.emissiveMap);

  /*
   * UV transform.
   */
  uniforms.uTextureScale.value.set(
    surface.textureScale[0],
    surface.textureScale[1],
  );

  uniforms.uTextureRotation.value = surface.textureRotation;

  /*
   * Toon.
   */
  uniforms.uBands.value = Math.max(1, bands);

  return (
    <shaderMaterial
      vertexShader={vertexShader}

      fragmentShader={fragmentShader}

      uniforms={uniforms}

      side={THREE.DoubleSide}

      extensions={{
        derivatives: true,
      }}
    />
  );
}
